// Short lightning bolt between two points — used for Mundo's ult zap ticks.

import type { Animation } from "../../engine/animation/AnimationQueue";
import type { Point } from "../../engine/hex/HexLayout";

const DURATION = 280;
const SEGMENTS = 6;

export class LightningZapVFX implements Animation {
  private elapsed = 0;
  private offsets: number[];

  constructor(
    private from: Point,
    private to: Point,
  ) {
    this.offsets = Array.from({ length: SEGMENTS - 1 }, () => (Math.random() - 0.5) * 18);
  }

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, DURATION);
    // Jitter each frame for a flickering effect
    if (this.elapsed < DURATION * 0.75) {
      this.offsets = this.offsets.map(() => (Math.random() - 0.5) * 18);
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const t = this.elapsed / DURATION;
    const fade = t < 0.5 ? 1 : 1 - (t - 0.5) / 0.5;

    const dx = this.to.x - this.from.x;
    const dy = this.to.y - this.from.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len === 0) return;
    const nx = -dy / len;
    const ny = dx / len;

    ctx.save();

    // Outer glow stroke
    ctx.globalAlpha = fade * 0.45;
    ctx.strokeStyle = "#a5f3fc";
    ctx.lineWidth = 5;
    ctx.shadowColor = "#38bdf8";
    ctx.shadowBlur = 18;
    ctx.beginPath();
    this.drawBolt(ctx, nx, ny);
    ctx.stroke();

    // Core bright stroke
    ctx.globalAlpha = fade * 0.9;
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 1.5;
    ctx.shadowColor = "#7dd3fc";
    ctx.shadowBlur = 8;
    ctx.beginPath();
    this.drawBolt(ctx, nx, ny);
    ctx.stroke();

    ctx.restore();
  }

  private drawBolt(ctx: CanvasRenderingContext2D, nx: number, ny: number): void {
    const total = SEGMENTS;
    ctx.moveTo(this.from.x, this.from.y);
    for (let i = 1; i < total; i++) {
      const f = i / total;
      const bx = this.from.x + (this.to.x - this.from.x) * f;
      const by = this.from.y + (this.to.y - this.from.y) * f;
      const off = this.offsets[i - 1] ?? 0;
      ctx.lineTo(bx + nx * off, by + ny * off);
    }
    ctx.lineTo(this.to.x, this.to.y);
  }

  isDone(): boolean {
    return this.elapsed >= DURATION;
  }
}
